import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddIcon from '@mui/icons-material/Add';

import MainCard from 'components/MainCard';
import useGet from 'hooks/useGet';
import apiClient from 'utils/apiClient';

import {
  formatCurrency,
  formatDateTime,
  getCollection,
  normalizeProduct,
  priceRowsToMap,
  resolveProductImage,
  splitLines,
  toPriceRows
} from './helpers';

const visibilityOptions = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Published' },
  { value: 'false', label: 'Unpublished' }
];

const activityOptions = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' }
];

const createEmptyForm = () => ({
  id: '',
  SKU: '',
  title: '',
  description: '',
  defaultCurrency: 'INR',
  priceRows: [{ currency: 'INR', amount: '' }],
  payNow: true,
  isPublished: false,
  isActive: true,
  heroImageUrl: '',
  galleryImagesText: '',
  heroImageFile: null,
  galleryImageFiles: []
});

const ProductFormDialog = ({
  form,
  open,
  saving,
  error,
  onClose,
  onSave,
  onChange,
  onPriceRowChange,
  onAddPriceRow,
  onRemovePriceRow,
  onHeroFileChange,
  onGalleryFilesChange
}) => {
  const [heroPreview, setHeroPreview] = useState('');
  const [galleryPreviewUrls, setGalleryPreviewUrls] = useState([]);

  useEffect(() => {
    const nextHeroPreview = form.heroImageFile
      ? URL.createObjectURL(form.heroImageFile)
      : form.heroImageUrl;

    const nextGalleryPreviewUrls = form.galleryImageFiles.length
      ? form.galleryImageFiles.map((file) => URL.createObjectURL(file))
      : splitLines(form.galleryImagesText);

    setHeroPreview(nextHeroPreview || '');
    setGalleryPreviewUrls(nextGalleryPreviewUrls);

    return () => {
      if (form.heroImageFile && nextHeroPreview) {
        URL.revokeObjectURL(nextHeroPreview);
      }
      if (form.galleryImageFiles.length) {
        nextGalleryPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
      }
    };
  }, [
    form.galleryImageFiles,
    form.galleryImagesText,
    form.heroImageFile,
    form.heroImageUrl
  ]);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{form.id ? 'Edit Custom Product' : 'Add Custom Product'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="SKU"
              value={form.SKU}
              onChange={(event) => onChange('SKU', event.target.value.toUpperCase())}
            />
            <TextField
              fullWidth
              label="Title"
              value={form.title}
              onChange={(event) => onChange('title', event.target.value)}
            />
          </Stack>

          <TextField
            label="Description"
            value={form.description}
            onChange={(event) => onChange('description', event.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
              }
            }}
            multiline
            minRows={3}
            sx={{ '& textarea': { resize: 'vertical' } }}
          />

          <Stack spacing={1.25}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1">Pricing</Typography>
              <Button onClick={onAddPriceRow} size="small">
                Add Currency
              </Button>
            </Stack>
            {form.priceRows.map((row, index) => (
              <Stack key={`${row.currency}-${index}`} direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  label="Currency"
                  value={row.currency}
                  onChange={(event) =>
                    onPriceRowChange(index, 'currency', event.target.value.toUpperCase())
                  }
                  sx={{ minWidth: 160 }}
                />
                <TextField
                  fullWidth
                  type="number"
                  label="Amount"
                  value={row.amount}
                  onChange={(event) => onPriceRowChange(index, 'amount', event.target.value)}
                />
                <Button
                  color="error"
                  onClick={() => onRemovePriceRow(index)}
                  disabled={form.priceRows.length === 1}
                >
                  Remove
                </Button>
              </Stack>
            ))}
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="Default Currency"
              value={form.defaultCurrency}
              onChange={(event) => onChange('defaultCurrency', event.target.value.toUpperCase())}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.payNow}
                  onChange={(event) => onChange('payNow', event.target.checked)}
                />
              }
              label="Pay Now"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isPublished}
                  onChange={(event) => onChange('isPublished', event.target.checked)}
                />
              }
              label="Published"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(event) => onChange('isActive', event.target.checked)}
                />
              }
              label="Active"
            />
          </Stack>

          <TextField
            label="Hero Image URL"
            value={form.heroImageUrl}
            onChange={(event) => onChange('heroImageUrl', event.target.value)}
            helperText="Keep the current URL, clear it to remove, or upload a new hero image below."
          />

          <Button component="label" variant="outlined">
            Upload Hero Image
            <input
              hidden
              type="file"
              accept="image/*"
              onChange={(event) => onHeroFileChange(event.target.files?.[0] || null)}
            />
          </Button>

          {heroPreview ? (
            <Box
              component="img"
              src={heroPreview}
              alt="Hero preview"
              sx={{
                width: 120,
                height: 120,
                objectFit: 'cover',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            />
          ) : null}

          <TextField
            label="Gallery Images"
            value={form.galleryImagesText}
            onChange={(event) => onChange('galleryImagesText', event.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
              }
            }}
            helperText="Paste gallery image URLs separated by comma or new line, or upload gallery images below."
            multiline
            minRows={3}
            sx={{ '& textarea': { resize: 'vertical' } }}
          />

          <Button component="label" variant="outlined">
            Upload Gallery Images
            <input
              hidden
              type="file"
              accept="image/*"
              multiple
              onChange={(event) => onGalleryFilesChange(Array.from(event.target.files || []))}
            />
          </Button>

          {galleryPreviewUrls.length ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {galleryPreviewUrls.map((url, index) => (
                <Box
                  key={`${url}-${index}`}
                  component="img"
                  src={url}
                  alt={`Gallery preview ${index + 1}`}
                  sx={{
                    width: 88,
                    height: 88,
                    objectFit: 'cover',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                />
              ))}
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onSave} disabled={saving}>
          {saving ? 'Saving...' : form.id ? 'Save Changes' : 'Create Product'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default function CustomProductsList() {
  const [search, setSearch] = useState('');
  const [publishedFilter, setPublishedFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(createEmptyForm());
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');

  const endpoint = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page + 1));
    params.set('limit', String(rowsPerPage));
    if (search.trim()) params.set('q', search.trim());
    if (publishedFilter) params.set('isPublished', publishedFilter);
    if (activeFilter) params.set('isActive', activeFilter);
    return `/api/admin/custom-products?${params.toString()}`;
  }, [activeFilter, page, publishedFilter, rowsPerPage, search]);

  const { data, error, loading, mutate } = useGet(endpoint);
  const products = getCollection(data, ['products']).map(normalizeProduct);

  const openCreateDialog = () => {
    setSaveError('');
    setForm(createEmptyForm());
    setDialogOpen(true);
  };

  const openEditDialog = (product) => {
    const normalized = normalizeProduct(product);
    setSaveError('');
    setForm({
      id: normalized.id,
      SKU: normalized.SKU,
      title: normalized.title,
      description: normalized.description,
      defaultCurrency: normalized.defaultCurrency,
      priceRows: toPriceRows(normalized.priceByCurrency),
      payNow: normalized.payNow,
      isPublished: normalized.isPublished,
      isActive: normalized.isActive,
      heroImageUrl: normalized.media?.heroImage || '',
      galleryImagesText: normalized.media?.galleryImages?.join('\n') || '',
      heroImageFile: null,
      galleryImageFiles: []
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setForm(createEmptyForm());
  };

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const updatePriceRow = (index, key, value) => {
    setForm((current) => ({
      ...current,
      priceRows: current.priceRows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [key]: value } : row
      )
    }));
  };

  const addPriceRow = () => {
    setForm((current) => ({
      ...current,
      priceRows: [...current.priceRows, { currency: '', amount: '' }]
    }));
  };

  const removePriceRow = (index) => {
    setForm((current) => ({
      ...current,
      priceRows: current.priceRows.filter((_, rowIndex) => rowIndex !== index)
    }));
  };

  const buildPayload = () => {
    const payload = new FormData();
    const priceByCurrency = priceRowsToMap(form.priceRows);

    payload.append('SKU', form.SKU.trim().toUpperCase());
    payload.append('title', form.title.trim());
    payload.append('description', form.description);
    payload.append('defaultCurrency', form.defaultCurrency.trim().toUpperCase());
    payload.append('priceByCurrency', JSON.stringify(priceByCurrency));
    payload.append('payNow', String(Boolean(form.payNow)));
    payload.append('isPublished', String(Boolean(form.isPublished)));
    payload.append('isActive', String(Boolean(form.isActive)));
    payload.append('heroImage', form.heroImageUrl.trim());
    payload.append('galleryImages', JSON.stringify(splitLines(form.galleryImagesText)));

    if (form.heroImageFile) {
      payload.append('heroImage', form.heroImageFile);
    }

    form.galleryImageFiles.forEach((file) => {
      payload.append('galleryImages', file);
    });

    return payload;
  };

  const onSave = async () => {
    setSaveError('');
    setActionError('');
    setActionSuccess('');
    setSaving(true);

    try {
      const payload = buildPayload();
      if (form.id) {
        await apiClient.put(`/api/admin/custom-products/${form.id}`, payload);
        setActionSuccess('Custom product updated successfully.');
      } else {
        await apiClient.post('/api/admin/custom-products', payload);
        setActionSuccess('Custom product created successfully.');
      }
      setDialogOpen(false);
      setForm(createEmptyForm());
      await mutate();
    } catch (requestError) {
      setSaveError(requestError?.response?.data?.message || 'Failed to save custom product.');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (product) => {
    const confirmed = window.confirm(`Delete "${product.title}"?`);
    if (!confirmed) return;

    setActionError('');
    setActionSuccess('');

    try {
      await apiClient.delete(`/api/admin/custom-products/${product.id}`);
      setActionSuccess('Custom product deleted successfully.');
      await mutate();
    } catch (requestError) {
      setActionError(
        requestError?.response?.data?.message || 'Failed to delete custom product.'
      );
    }
  };

  const toggleFlag = async (productId, key) => {
    setActionError('');
    setActionSuccess('');

    try {
      await apiClient.patch(`/api/admin/custom-products/${productId}/${key}`);
      setActionSuccess(
        key === 'published'
          ? 'Publish status updated successfully.'
          : 'Active status updated successfully.'
      );
      await mutate();
    } catch (requestError) {
      setActionError(
        requestError?.response?.data?.message || 'Failed to update product status.'
      );
    }
  };

  return (
    <Stack spacing={2.5}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={1.5}
      >
        <Box>
          <Typography variant="h4">Custom Products</Typography>
          <Typography color="text.secondary">
            Manage catalog items, pricing, media, and publish state.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
          Add Product
        </Button>
      </Stack>

      {actionError ? <Alert severity="error">{actionError}</Alert> : null}
      {actionSuccess ? <Alert severity="success">{actionSuccess}</Alert> : null}
      {error ? <Alert severity="error">Failed to load custom products.</Alert> : null}

      <MainCard>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            fullWidth
            size="small"
            label="Search by title, SKU, or description"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
          />
          <TextField
            select
            size="small"
            label="Published"
            value={publishedFilter}
            onChange={(event) => {
              setPublishedFilter(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 160 }}
          >
            {visibilityOptions.map((option) => (
              <MenuItem key={option.value || 'all'} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Active"
            value={activeFilter}
            onChange={(event) => {
              setActiveFilter(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 160 }}
          >
            {activityOptions.map((option) => (
              <MenuItem key={option.value || 'all'} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </MainCard>

      <MainCard title={`Products (${Number(data?.total || products.length)})`}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Pay Now</TableCell>
                <TableCell>Published</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Loading custom products...
                  </TableCell>
                </TableRow>
              ) : products.length ? (
                products.map((product) => {
                  const image = resolveProductImage(product);
                  const price =
                    product.priceByCurrency?.[product.defaultCurrency] ??
                    Object.values(product.priceByCurrency || {})[0] ??
                    0;

                  return (
                    <TableRow key={product.id} hover>
                      <TableCell sx={{ maxWidth: 380, minWidth: 220 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar
                            src={image || undefined}
                            variant="rounded"
                            sx={{ width: 48, height: 48, flexShrink: 0 }}
                          >
                            {product.title?.charAt(0) || 'C'}
                          </Avatar>
                          <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block'
                              }}
                              title={product.title}
                            >
                              {product.title}
                            </Typography>
                            {product.description ? (
                              <Tooltip title={product.description} placement="bottom-start" arrow>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    mt: 0.25
                                  }}
                                >
                                  {product.description}
                                </Typography>
                              </Tooltip>
                            ) : null}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>{product.SKU || '-'}</TableCell>
                      <TableCell>{formatCurrency(product.defaultCurrency, price)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={product.payNow ? 'success' : 'default'}
                          label={product.payNow ? 'Enabled' : 'Disabled'}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={product.isPublished}
                          onChange={() => toggleFlag(product.id, 'published')}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={product.isActive}
                          onChange={() => toggleFlag(product.id, 'active')}
                        />
                      </TableCell>
                      <TableCell>{formatDateTime(product.updatedAt || product.createdAt)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton onClick={() => openEditDialog(product)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton color="error" onClick={() => onDelete(product)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No custom products found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={Number(data?.total || 0)}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </MainCard>

      <ProductFormDialog
        open={dialogOpen}
        form={form}
        saving={saving}
        error={saveError}
        onClose={closeDialog}
        onSave={onSave}
        onChange={updateForm}
        onPriceRowChange={updatePriceRow}
        onAddPriceRow={addPriceRow}
        onRemovePriceRow={removePriceRow}
        onHeroFileChange={(file) => updateForm('heroImageFile', file)}
        onGalleryFilesChange={(files) => updateForm('galleryImageFiles', files)}
      />
    </Stack>
  );
}
